// ─── CSV Utility ────────────────────────────────────────────────
// Minimal utilities for CSV file validation and download.
// All CSV parsing/processing is handled by the backend.

// ─── Constants ──────────────────────────────────────────────────

export const EXPECTED_CSV_COLUMNS = [
	'serviceid',
	'servicename',
	'description',
	'status',
	'size',
	'length',
	'durationminutes',
	'price',
] as const;

// ─── Types ──────────────────────────────────────────────────────

export interface CsvValidationResult {
	valid: boolean;
	serviceCount: number;
	totalRows: number;
	error?: string;
}

// ─── CSV Line Parser ────────────────────────────────────────────

function parseCsvLine(line: string): string[] {
	const result: string[] = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < line.length; i++) {
		const ch = line[i];
		if (inQuotes) {
			if (ch === '"' && line[i + 1] === '"') {
				current += '"';
				i++;
			} else if (ch === '"') {
				inQuotes = false;
			} else {
				current += ch;
			}
		} else {
			if (ch === '"') {
				inQuotes = true;
			} else if (ch === ',') {
				result.push(current.trim());
				current = '';
			} else {
				current += ch;
			}
		}
	}
	result.push(current.trim());
	return result;
}

// ─── File Validation ────────────────────────────────────────────

export function validateServiceCsvFile(file: File): Promise<CsvValidationResult> {
	return new Promise(resolve => {
		if (!file.name.toLowerCase().endsWith('.csv')) {
			resolve({
				valid: false,
				serviceCount: 0,
				totalRows: 0,
				error: 'Please upload a CSV file.',
			});
			return;
		}

		const reader = new FileReader();
		reader.onload = e => {
			const text = e.target?.result as string;
			if (!text || text.trim().length === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file is empty.',
				});
				return;
			}

			const lines = text
				.split('\n')
				.map(l => l.trim())
				.filter(l => l.length > 0);
			if (lines.length < 2) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file has no data rows.',
				});
				return;
			}

			// Check header
			const header = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ''));
			const missing = EXPECTED_CSV_COLUMNS.filter(col => !header.includes(col));
			if (missing.length > 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: `Missing required columns: ${missing.join(', ')}`,
				});
				return;
			}

			// Count data rows and unique service names
			const serviceNameIdx = header.indexOf('servicename');
			const serviceNames = new Set<string>();
			let totalRows = 0;

			for (let i = 1; i < lines.length; i++) {
				const cells = parseCsvLine(lines[i]);
				const name = cells[serviceNameIdx]?.trim();
				if (name) {
					serviceNames.add(name.toLowerCase());
					totalRows++;
				}
			}

			if (totalRows === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'No valid data rows found in CSV.',
				});
				return;
			}

			resolve({ valid: true, serviceCount: serviceNames.size, totalRows });
		};

		reader.onerror = () => {
			resolve({ valid: false, serviceCount: 0, totalRows: 0, error: 'Failed to read file.' });
		};

		reader.readAsText(file);
	});
}

// ─── Task CSV Validation ────────────────────────────────────────

export const EXPECTED_TASK_CSV_COLUMNS = [
	'servicetaskid',
	'serviceid',
	'servicename',
	'taskid',
	'taskname',
	'size',
	'length',
	'durationminutes',
	'price',
] as const;

export function validateTaskCsvFile(file: File): Promise<CsvValidationResult> {
	return new Promise(resolve => {
		if (!file.name.toLowerCase().endsWith('.csv')) {
			resolve({
				valid: false,
				serviceCount: 0,
				totalRows: 0,
				error: 'Please upload a CSV file.',
			});
			return;
		}

		const reader = new FileReader();
		reader.onload = e => {
			const text = e.target?.result as string;
			if (!text || text.trim().length === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file is empty.',
				});
				return;
			}

			const lines = text
				.split('\n')
				.map(l => l.trim())
				.filter(l => l.length > 0);
			if (lines.length < 2) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file has no data rows.',
				});
				return;
			}

			const header = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ''));
			const missing = EXPECTED_TASK_CSV_COLUMNS.filter(col => !header.includes(col));
			if (missing.length > 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: `Missing required columns: ${missing.join(', ')}`,
				});
				return;
			}

			const taskNameIdx = header.indexOf('taskname');
			const taskNames = new Set<string>();
			let totalRows = 0;

			for (let i = 1; i < lines.length; i++) {
				const cells = parseCsvLine(lines[i]);
				const taskName = cells[taskNameIdx]?.trim();
				if (taskName) {
					taskNames.add(taskName.toLowerCase());
					totalRows++;
				}
			}

			if (totalRows === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'No valid data rows found in CSV.',
				});
				return;
			}

			resolve({ valid: true, serviceCount: taskNames.size, totalRows });
		};

		reader.onerror = () => {
			resolve({ valid: false, serviceCount: 0, totalRows: 0, error: 'Failed to read file.' });
		};

		reader.readAsText(file);
	});
}

// ─── Download Helpers ────────────────────────────────────────────

/**
 * Trigger a browser download of CSV content (plain string from the API).
 */
export function downloadCsvString(csvContent: string, fileName: string): void {
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	a.click();
	URL.revokeObjectURL(url);
}

// ─── Customer Import CSV Validation ─────────────────────────────
// Mirrors the backend's header matching: columns are found by name, not position,
// after lower-casing and dropping everything but letters.

const CUSTOMER_NAME_HEADERS = ['name', 'fullname', 'customername', 'customer'];
const CUSTOMER_EMAIL_HEADERS = ['email', 'emailaddress', 'customeremail'];
const CUSTOMER_PHONE_HEADERS = [
	'phone',
	'phonenumber',
	'phoneno',
	'mobile',
	'mobilenumber',
	'cell',
	'cellphone',
	'telephone',
];

export const CUSTOMER_IMPORT_MAX_ROWS = 2000;

export interface CustomerCsvValidationResult {
	valid: boolean;
	/** Data rows in the file (header excluded). */
	totalRows: number;
	/** Rows missing name, email or phone. The backend rejects these one by one. */
	incompleteRows: number;
	error?: string;
}

export function validateCustomerCsvFile(file: File): Promise<CustomerCsvValidationResult> {
	const fail = (error: string): CustomerCsvValidationResult => ({
		valid: false,
		totalRows: 0,
		incompleteRows: 0,
		error,
	});

	return new Promise(resolve => {
		if (!file.name.toLowerCase().endsWith('.csv')) {
			resolve(fail('Please upload a CSV file.'));
			return;
		}

		const reader = new FileReader();
		reader.onload = e => {
			const text = (e.target?.result as string | null) ?? '';
			if (text.trim().length === 0) {
				resolve(fail('CSV file is empty.'));
				return;
			}

			const lines = text
				.replace(/^\uFEFF/, '')
				.split('\n')
				.map(l => l.trim())
				.filter(l => l.length > 0);
			if (lines.length < 2) {
				resolve(fail('CSV file has no data rows.'));
				return;
			}

			// "DOB (YYYY-MM-DD)" -> "dob": drop any bracketed hint, then everything but letters.
			const header = parseCsvLine(lines[0]).map(h =>
				h
					.replace(/\([^)]*\)/g, '')
					.toLowerCase()
					.replace(/[^a-z]/g, '')
			);
			const nameIdx = header.findIndex(h => CUSTOMER_NAME_HEADERS.includes(h));
			const emailIdx = header.findIndex(h => CUSTOMER_EMAIL_HEADERS.includes(h));
			const phoneIdx = header.findIndex(h => CUSTOMER_PHONE_HEADERS.includes(h));

			const missing: string[] = [];
			if (nameIdx === -1) missing.push('Name');
			if (emailIdx === -1) missing.push('Email');
			if (phoneIdx === -1) missing.push('Phone');
			if (missing.length > 0) {
				resolve(fail(`Missing required column(s): ${missing.join(', ')}`));
				return;
			}

			const totalRows = lines.length - 1;
			if (totalRows > CUSTOMER_IMPORT_MAX_ROWS) {
				resolve(
					fail(
						`This file has ${totalRows} rows. A single import is limited to ${CUSTOMER_IMPORT_MAX_ROWS} customers; please split it.`
					)
				);
				return;
			}

			let incompleteRows = 0;
			for (let i = 1; i < lines.length; i++) {
				const cells = parseCsvLine(lines[i]);
				const has = (idx: number) => (cells[idx] ?? '').trim().length > 0;
				if (!has(nameIdx) || !has(emailIdx) || !has(phoneIdx)) incompleteRows++;
			}

			resolve({ valid: true, totalRows, incompleteRows });
		};

		reader.onerror = () => resolve(fail('Failed to read file.'));
		reader.readAsText(file);
	});
}
